/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, spriteUrl } from "@/lib/api";
import { useCountdown } from "@/lib/time";
import { Dumbbell, ArrowRightLeft, Filter, Search, ArrowDownAZ, CheckSquare, Square, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import PokemonDetailModal from "@/components/PokemonDetailModal";

const POKEMON_TYPES = [
  { value: "normal", label: "Normal" },
  { value: "fire", label: "Feu" },
  { value: "water", label: "Eau" },
  { value: "grass", label: "Plante" },
  { value: "electric", label: "Électrik" },
  { value: "ice", label: "Glace" },
  { value: "fighting", label: "Combat" },
  { value: "poison", label: "Poison" },
  { value: "ground", label: "Sol" },
  { value: "flying", label: "Vol" },
  { value: "psychic", label: "Psy" },
  { value: "bug", label: "Insecte" },
  { value: "rock", label: "Roche" },
  { value: "ghost", label: "Spectre" },
  { value: "dragon", label: "Dragon" },
  { value: "dark", label: "Ténèbres" },
  { value: "steel", label: "Acier" },
  { value: "fairy", label: "Fée" },
];

// Mirror of backend `EVOLUTIONS` filtered on `by=stone` entries — pokemon_id → [stone_item_id, …].
// Used to show which stones a PC pokémon can currently evolve with.
const STONE_EVOLUTION_TABLE = {
  25:  ["pierre_foudre"],
  30:  ["pierre_lune"],
  33:  ["pierre_lune"],
  35:  ["pierre_lune"],
  37:  ["pierre_feu"],
  39:  ["pierre_lune"],
  44:  ["pierre_plante", "pierre_soleil"],
  58:  ["pierre_feu"],
  61:  ["pierre_eau", "pierre_royale"],
  70:  ["pierre_plante"],
  90:  ["pierre_eau"],
  95:  ["peau_metal"],
  102: ["pierre_plante"],
  117: ["ecaille_draco"],
  120: ["pierre_eau"],
  123: ["peau_metal"],
  133: ["pierre_eau", "pierre_foudre", "pierre_feu"],
  191: ["pierre_soleil"],
};

export default function PCPage() {
  const [items, setItems] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [filterTrainerId, setFilterTrainerId] = useState("");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // recent | name | level | iv
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = filterTrainerId ? { trainer_id: filterTrainerId } : {};
      const [p, t, inv] = await Promise.all([
        api.get("/pc", { params }),
        api.get("/trainers"),
        api.get("/inventory"),
      ]);
      setItems(p.data); setTrainers(t.data);
      setInventory(Array.isArray(inv.data) ? inv.data : (inv.data?.items || []));
    } catch { /* ignore */ }
  }, [filterTrainerId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const visible = useMemo(() => {
    let arr = [...items];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((p) => p.name?.toLowerCase().includes(q));
    }
    if (filterType) {
      arr = arr.filter((p) => (p.types || []).some((t) => t.toLowerCase() === filterType.toLowerCase()));
    }
    if (filterRegion) {
      arr = arr.filter((p) => (p.region || "kanto") === filterRegion);
    }
    if (sortBy === "name") arr.sort((a, b) => a.name.localeCompare(b.name, "fr"));
    else if (sortBy === "level") arr.sort((a, b) => (b.level || 0) - (a.level || 0));
    else if (sortBy === "iv") arr.sort((a, b) => (b.iv || 91) - (a.iv || 91));
    else arr.sort((a, b) => (b.caught_at || "").localeCompare(a.caught_at || ""));
    return arr;
  }, [items, search, filterType, filterRegion, sortBy]);

  const train = async (id) => {
    try { await api.post(`/pc/${id}/train`); toast.success("Entraînement lancé (2 min)"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const transfer = async (id, trainerId) => {
    try { await api.post(`/pc/${id}/transfer/${trainerId}`); toast.success("Pokémon transféré"); setTransferTarget(null); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const release = async (id) => {
    try {
      await api.delete(`/pc/${id}`);
      toast.success("Pokémon relâché dans la nature.");
      setDetailTarget(null);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const releaseBulk = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/pc/release-bulk", { ids: [...selectedIds] });
      const { released, skipped_training } = res.data;
      if (released > 0) toast.success(`${released} Pokémon relâché(s).`);
      if (skipped_training > 0) toast.warning(`${skipped_training} en entraînement — non relâchés.`);
      setSelectedIds(new Set());
      setSelectMode(false);
      setConfirmBulk(false);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const evolveWithStone = async (pokeId, stoneId) => {
    try {
      const res = await api.post("/inventory/use_stone", {
        stone_id: stoneId,
        target_kind: "pc",
        pc_pokemon_id: pokeId,
      });
      toast.success(`Évolution : ${res.data.new_name} !`);
      setDetailTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Compute the list of stones available in the user's inventory that can
  // evolve the currently opened PC pokémon.
  const stonesForOpened = useMemo(() => {
    if (!detailTarget) return [];
    const pid = detailTarget.pokemon_id;
    const evoMap = STONE_EVOLUTION_TABLE[pid] || [];
    return inventory
      .filter((it) => it.type === "evolution" && evoMap.includes(it.item_id))
      .map((it) => ({ item_id: it.item_id, name: it.name, qty: it.qty }));
  }, [detailTarget, inventory]);

  return (
    <div data-testid="pc-page">
      <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
        <div>
          <div className="overline">Système de stockage</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">PC du labo</h1>
          <p className="text-xs text-[#4A4A4A] mt-2 max-w-2xl">
            Uniquement les Pokémon entreposés au labo — les Pokémon en équipe d&apos;un dresseur n&apos;apparaissent pas ici.
            Entraînez-les ou transférez-les dans l&apos;équipe d&apos;un dresseur (6 max).
          </p>
        </div>
        <div className="panel px-3 py-2 shrink-0 flex items-center gap-3">
          <div>
            <div className="overline">Stockés</div>
            <div className="font-serif-display text-3xl leading-none" data-testid="pc-count">{visible.length}/{items.length}</div>
          </div>
          <button
            data-testid="pc-select-toggle"
            className={`btn-ink text-[10px] py-1 px-2 whitespace-nowrap ${selectMode ? "btn-leaf" : ""}`}
            onClick={() => {
              setSelectMode((v) => !v);
              setSelectedIds(new Set());
            }}
          >
            {selectMode ? <><X size={11}/> Annuler sélection</> : <><CheckSquare size={11}/> Sélectionner</>}
          </button>
        </div>
      </div>

      {selectMode && (
        <div
          className="panel p-2 mb-3 flex items-center justify-between flex-wrap gap-2"
          style={{ background: "#FFF4D9", borderColor: "#B87333" }}
          data-testid="pc-select-toolbar"
        >
          <div className="font-mono text-[11px] text-[#1A1A1A]">
            <strong>{selectedIds.size}</strong> Pokémon sélectionné(s) sur {visible.length} visibles.
          </div>
          <div className="flex gap-2">
            <button
              data-testid="pc-select-all"
              className="btn-ink text-[10px] py-1 px-2"
              onClick={() => setSelectedIds(new Set(visible.filter((p) => !p.training).map((p) => p.id)))}
            >
              <CheckSquare size={10}/> Tout cocher (visible)
            </button>
            <button
              data-testid="pc-select-none"
              className="btn-ink text-[10px] py-1 px-2"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
            >
              <Square size={10}/> Tout décocher
            </button>
            <button
              data-testid="pc-bulk-release"
              className="btn-ink btn-accent text-[10px] py-1 px-2"
              disabled={selectedIds.size === 0}
              onClick={() => setConfirmBulk(true)}
            >
              <Trash2 size={10}/> Relâcher les {selectedIds.size} sélectionnés
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="panel p-3 mb-4 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <div className="col-span-2 md:col-span-2">
          <div className="overline text-[9px] mb-1 flex items-center gap-1"><Search size={10}/> Rechercher</div>
          <input
            data-testid="pc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom du Pokémon..."
            className="w-full ink-input text-[11px] py-1"
          />
        </div>
        <div>
          <div className="overline text-[9px] mb-1"><Filter size={10} className="inline"/> Dresseur</div>
          <select
            data-testid="pc-filter-trainer"
            className="ink-input text-[11px] py-1 w-full"
            value={filterTrainerId}
            onChange={(e) => setFilterTrainerId(e.target.value)}
          >
            <option value="">Tous</option>
            {trainers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <div className="overline text-[9px] mb-1">Type</div>
          <select
            data-testid="pc-filter-type"
            className="ink-input text-[11px] py-1 w-full"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Tous</option>
            {POKEMON_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <div className="overline text-[9px] mb-1">Région</div>
          <select
            data-testid="pc-filter-region"
            className="ink-input text-[11px] py-1 w-full"
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
          >
            <option value="">Toutes</option>
            <option value="kanto">Kanto</option>
            <option value="johto">Johto</option>
          </select>
        </div>
        <div>
          <div className="overline text-[9px] mb-1"><ArrowDownAZ size={10} className="inline"/> Trier par</div>
          <select
            data-testid="pc-sort"
            className="ink-input text-[11px] py-1 w-full"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Plus récent</option>
            <option value="name">Nom (A-Z)</option>
            <option value="level">Niveau ↓</option>
            <option value="iv">IV ↓</option>
          </select>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="panel p-8 text-center text-[#7A7A7A] font-mono text-sm">
          Aucun Pokémon ne correspond à ces filtres.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="pc-grid">
          {visible.map((p) => (
            <PCCard
              key={p.id}
              poke={p}
              selectMode={selectMode}
              selected={selectedIds.has(p.id)}
              onToggleSelect={() => toggleSelected(p.id)}
              onTrain={() => train(p.id)}
              onTransferOpen={() => setTransferTarget(p)}
              onOpenDetail={() => setDetailTarget(p)}
            />
          ))}
        </div>
      )}

      {transferTarget && (
        <TransferModal
          poke={transferTarget}
          trainers={trainers}
          onClose={() => setTransferTarget(null)}
          onTransfer={(tid) => transfer(transferTarget.id, tid)}
        />
      )}

      {detailTarget && (
        <PokemonDetailModal
          poke={detailTarget}
          context="pc"
          availableStones={stonesForOpened}
          onEvolve={(stoneId) => evolveWithStone(detailTarget.id, stoneId)}
          onClose={() => setDetailTarget(null)}
          onTrain={() => { setDetailTarget(null); train(detailTarget.id); }}
          onRelease={() => release(detailTarget.id)}
        />
      )}

      {confirmBulk && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ background: "rgba(26,26,26,0.55)", zIndex: 70 }}
          onClick={() => !bulkBusy && setConfirmBulk(false)}
          data-testid="pc-bulk-confirm-modal"
        >
          <div className="panel-strong p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="overline">Confirmation</div>
            <h3 className="font-serif-display text-2xl leading-tight mt-1 mb-2">
              Relâcher {selectedIds.size} Pokémon ?
            </h3>
            <p className="font-mono text-[11px] text-[#4A4A4A] mb-4 leading-snug">
              Cette action est <strong>irréversible</strong>. Les Pokémon en entraînement seront
              automatiquement ignorés.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="pc-bulk-confirm-cancel"
                className="btn-ink text-[11px] py-1 px-3"
                onClick={() => setConfirmBulk(false)}
                disabled={bulkBusy}
              >
                Annuler
              </button>
              <button
                data-testid="pc-bulk-confirm-ok"
                className="btn-ink btn-accent text-[11px] py-1 px-3"
                onClick={releaseBulk}
                disabled={bulkBusy}
              >
                {bulkBusy ? "…" : "Relâcher"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PCCard({ poke, onTrain, onTransferOpen, onOpenDetail, selectMode = false, selected = false, onToggleSelect }) {
  const { label: timer, urgent } = useCountdown(poke.training_ends_at);
  const isShiny = poke.is_shiny;
  const spriteSrc = isShiny
    ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${poke.pokemon_id}.png`
    : spriteUrl(poke.pokemon_id);
  const xpNeeded = poke.level * 30;
  const xpPct = Math.min(100, Math.round(((poke.xp || 0) / xpNeeded) * 100));
  const isLockedFromSelect = selectMode && poke.training;
  return (
    <div
      className={`panel p-3 flex flex-col items-center text-center cursor-pointer hover:bg-[#FCF9F2] relative ${isShiny ? "bg-[#FFF4D9]" : ""} ${
        selectMode && selected ? "ring-2 ring-[#B87333] bg-[#FFF4D9]" : ""
      } ${isLockedFromSelect ? "opacity-60" : ""}`}
      data-testid={`pc-card-${poke.id}`}
      onClick={(e) => {
        if (e.target.closest("button") || e.target.closest("[data-role='select-checkbox']")) return;
        if (selectMode) {
          if (!poke.training) onToggleSelect?.();
          return;
        }
        onOpenDetail?.();
      }}
    >
      {selectMode && (
        <div
          data-role="select-checkbox"
          data-testid={`pc-checkbox-${poke.id}`}
          className={`absolute top-1 left-1 w-5 h-5 flex items-center justify-center border-2 border-[#1E2D24] cursor-pointer ${
            selected ? "bg-[#B87333]" : "bg-[#FCF9F2]"
          } ${poke.training ? "opacity-40 cursor-not-allowed" : ""}`}
          title={poke.training ? "En entraînement — non sélectionnable" : selected ? "Décocher" : "Cocher"}
          onClick={(e) => {
            e.stopPropagation();
            if (!poke.training) onToggleSelect?.();
          }}
        >
          {selected && <span className="text-white font-bold text-sm leading-none">✓</span>}
        </div>
      )}
      {isShiny && (
        <span className="stamp-validated stamp-shiny" aria-hidden="true">Shiny</span>
      )}
      <div className="flex justify-between items-start w-full">
        <span className="font-mono text-[10px] text-[#7A7A7A]">#{poke.pokemon_id.toString().padStart(3, "0")}</span>
        <span className="font-mono text-[10px] text-[#4A4A4A]">Lv {poke.level}</span>
      </div>
      <img src={spriteSrc} alt={poke.name} className="pixelated w-20 h-20 my-1" />
      {/* XP mini-bar (regional accent) */}
      <div className="w-full h-[3px] bg-[#EAE4D9] border border-[#2B2B2B] mb-1" title={`XP ${poke.xp || 0}/${xpNeeded}`}>
        <div className="h-full" style={{ width: `${xpPct}%`, background: "var(--region-accent)" }}/>
      </div>
      <div className="font-serif-display text-lg leading-none">
        {poke.name}{isShiny ? " ✨" : ""}
        {poke.gender === "M" && <span className="text-[#2E5EAA] ml-1">♂</span>}
        {poke.gender === "F" && <span className="text-[#B8336A] ml-1">♀</span>}
      </div>
      {/* IV summary */}
      {(() => {
        const iv = poke.iv ?? null;
        if (iv === null || iv === undefined) return null;
        const pct = Math.round((iv / 186) * 100);
        const color = pct >= 85 ? "#2E4F32" : pct >= 60 ? "#B87333" : "#7A7A7A";
        return (
          <div
            className="mt-1 flex items-center gap-1 font-mono text-[10px]"
            data-testid={`pc-iv-${poke.id}`}
            title={`IV total ${iv}/186 · ${pct}%`}
          >
            <span className="overline text-[9px]">IV</span>
            <span style={{ color }} className="font-mono">{iv}/186</span>
            <span className="text-[#7A7A7A]">·</span>
            <span style={{ color }}>{pct}%</span>
          </div>
        );
      })()}
      <div className="text-[10px] font-mono text-[#7A7A7A] mt-0.5" data-testid={`pc-owner-${poke.id}`}>
        capturé par <span className="text-[#1A1A1A]">{poke.caught_by_trainer_name || "—"}</span>
      </div>
      {poke.training ? (
        <div className={`text-xs font-mono mt-2 ${urgent ? "timer-urgent" : "text-[#B87333]"}`} data-testid={`pc-timer-${poke.id}`}>
          Entraînement · {timer}
        </div>
      ) : (
        <div className="flex gap-1 mt-2 w-full">
          <button data-testid={`pc-train-btn-${poke.id}`} className="btn-ink btn-leaf flex-1 text-[10px] py-1 px-1 justify-center" onClick={onTrain}>
            <Dumbbell size={11}/> Entraîner
          </button>
          <button data-testid={`pc-transfer-btn-${poke.id}`} className="btn-ink flex-1 text-[10px] py-1 px-1 justify-center" onClick={onTransferOpen}>
            <ArrowRightLeft size={11}/> Donner
          </button>
        </div>
      )}
    </div>
  );
}

function TransferModal({ poke, trainers, onClose, onTransfer }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(26,26,26,0.5)", zIndex: 60 }}
      onClick={onClose}
      data-testid="transfer-modal"
    >
      <div className="panel-strong p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="overline">Transfert</div>
        <h3 className="font-serif-display text-2xl leading-none mt-1 mb-3">Donner {poke.name} à...</h3>
        {trainers.length === 0 ? (
          <p className="text-sm text-[#7A7A7A]">Aucun dresseur disponible.</p>
        ) : (
          <ul className="space-y-1 max-h-[300px] overflow-auto">
            {trainers.map((t) => (
              <li key={t.id}>
                <button
                  data-testid={`transfer-pick-${t.id}`}
                  disabled={(t.team?.length || 0) >= 6}
                  className="btn-ink w-full justify-between"
                  onClick={() => onTransfer(t.id)}
                >
                  <span>{t.name} · Niv {t.level}</span>
                  <span className="font-mono">{t.team?.length || 0}/6</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <button className="btn-ink btn-accent mt-4 w-full justify-center" onClick={onClose} data-testid="transfer-cancel">
          Annuler
        </button>
      </div>
    </div>
  );
}
